import { useEffect, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Autorenew,
  PowerSettingsNew,
  RestartAlt,
  Sensors,
  WifiTethering,
} from "@mui/icons-material";
import { useAuth } from "../../../auth/useAuth";
import { can } from "../../../auth/can";
import { APP_API_BASE } from "../../../config/api";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL?.replace(/\/$/, "") || `${APP_API_BASE}`;

const actionSx = {
  borderRadius: "14px",
  border: "1px solid #E2E8F0",
  background: "#FFFFFF",
  p: 2,
  height: "100%",
};

const statusTone = {
  PENDING: { bg: "#EFF6FF", color: "#1D4ED8" },
  SENT: { bg: "#E0F2FE", color: "#0369A1" },
  ACKNOWLEDGED: { bg: "#FEF3C7", color: "#B45309" },
  COMPLETED: { bg: "#DCFCE7", color: "#15803D" },
  FAILED: { bg: "#FEE2E2", color: "#B91C1C" },
  TIMED_OUT: { bg: "#FEE2E2", color: "#B91C1C" },
};

function formatTimestamp(value) {
  if (!value) return "Pending";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Pending";
  return date.toLocaleString();
}

function formatTimestampAdd530(value) {
  if (!value) return "Pending";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Pending";

  // Add 5 hours 30 minutes
  const adjustedDate = new Date(
    date.getTime() + (5 * 60 + 30) * 60 * 1000
  );

  return adjustedDate.toLocaleString();
}

function summarizePayload(command) {
  const payload = command?.payload || {};
  if (command?.command_type === "relay_control") {
    return `Relay ${payload.relay} -> ${payload.state ? "ON" : "OFF"}`;
  }
  if (command?.command_type === "ping") {
    return "Reachability check";
  }
  if (command?.command_type === "reboot") {
    return "Soft restart";
  }
  return Object.keys(payload).length ? JSON.stringify(payload) : "No payload";
}

function ActionTile({
  icon,
  title,
  desc,
  busy,
  primaryLabel,
  secondaryLabel,
  onPrimary,
  onSecondary,
  primaryDisabled = false,
  secondaryDisabled = false,
}) {
  return (
    <Box sx={actionSx}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: "12px",
            background: "linear-gradient(135deg,#1976d2,#42a5f5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            boxShadow: "0 10px 20px rgba(25,118,210,0.18)",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, color: "#0F172A", fontSize: 15 }}>
            {title}
          </Typography>
          <Typography sx={{ mt: 0.5, color: "#64748B", fontSize: 13 }}>
            {desc}
          </Typography>
        </Box>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Button
          variant="contained"
          size="small"
          onClick={onPrimary}
          disabled={busy || primaryDisabled}
          sx={{
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 700,
            boxShadow: "none",
          }}
        >
          {busy ? (
            <CircularProgress size={16} sx={{ color: "#fff" }} />
          ) : (
            primaryLabel
          )}
        </Button>
        {secondaryLabel ? (
          <Button
            variant="outlined"
            size="small"
            onClick={onSecondary}
            disabled={busy || secondaryDisabled}
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 700,
            }}
          >
            {secondaryLabel}
          </Button>
        ) : null}
      </Stack>
    </Box>
  );
}

export default function DeviceCommandsCard({ deviceId }) {
  const { activeOrg, permissions } = useAuth();
  const [commands, setCommands] = useState([]);
  const [topics, setTopics] = useState({
    command_topic: "",
    command_ack_topic: "",
  });
  const [relayCount, setRelayCount] = useState(null);
  const [signingMode, setSigningMode] = useState("NONE");
  const [hmacConfigured, setHmacConfigured] = useState(false);
  const [hmacSecret, setHmacSecret] = useState("");
  const [securitySaving, setSecuritySaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    msg: "",
    severity: "success",
  });

  const token = localStorage.getItem("authToken");
  const canView = can(permissions, "device.view");
  const canControl = can(permissions, "device.control");

  const fetchCommands = async (silent = false) => {
    if (!deviceId || !token || !canView) return;

    if (!silent) setLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE}/api/v1/devices/${deviceId}/commands`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
          },
          params: { limit: 12 },
        },
      );

      setCommands(Array.isArray(res.data?.commands) ? res.data.commands : []);
      setTopics({
        command_topic: res.data?.command_topic || "",
        command_ack_topic: res.data?.command_ack_topic || "",
      });
      const count = Number.parseInt(res.data?.relay_count, 10);
      setRelayCount(Number.isFinite(count) && count >= 0 ? count : 1);
      const securityRes = await axios.get(
        `https://iot-backend.trackingcoredata.com/api/v1/devices/${deviceId}/command-security`,
        { headers: { Authorization: `Bearer ${token}`, ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }) } },
      );
      setSigningMode(securityRes.data?.command_signing_mode || "NONE");
      setHmacConfigured(Boolean(securityRes.data?.hmac_key_configured));
    } catch (error) {
      console.error("Failed to fetch device commands", error);
      if (!silent) {
        setSnackbar({
          open: true,
          msg: "Unable to load device command history",
          severity: "error",
        });
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const saveCommandSecurity = async () => {
    if (!deviceId || !token || !canControl) return;
    try {
      setSecuritySaving(true);
      const res = await axios.put(
        `https://iot-backend.trackingcoredata.com/api/v1/devices/${deviceId}/command-security`,
        { command_signing_mode: signingMode, ...(hmacSecret.trim() && { hmac_secret: hmacSecret.trim() }) },
        { headers: { Authorization: `Bearer ${token}`, ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }) } },
      );
      setHmacConfigured(Boolean(res.data?.hmac_key_configured));
      setHmacSecret("");
      setSnackbar({
        open: true,
        msg: res.data?.generated_key
          ? `HMAC enabled. Provision this key to firmware: ${res.data.generated_key}`
          : `Command signing set to ${res.data?.command_signing_mode}.`,
        severity: "success",
      });
    } catch (error) {
      setSnackbar({ open: true, msg: error?.response?.data?.error || "Unable to update command signing", severity: "error" });
    } finally {
      setSecuritySaving(false);
    }
  };

  useEffect(() => {
    fetchCommands();
  }, [deviceId, activeOrg?.id, canView]);

  useEffect(() => {
    if (!canView || !deviceId || !token) return undefined;

    const hasLiveCommands = commands.some((command) =>
      ["PENDING", "SENT", "ACKNOWLEDGED"].includes(
        String(command?.status || "").toUpperCase(),
      ),
    );

    const intervalMs = hasLiveCommands ? 4000 : 12000;
    const intervalId = setInterval(() => {
      fetchCommands(true);
    }, intervalMs);

    return () => clearInterval(intervalId);
  }, [commands, deviceId, canView, token, activeOrg?.id]);

  const sendCommand = async (type, payload, loadingKey, successMessage) => {
    if (!deviceId || !token || !canControl) return;

    try {
      setActionLoading(loadingKey);
      await axios.post(
        `${API_BASE}/api/v1/devices/${deviceId}/commands`,
        { type, payload },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
          },
        },
      );

      setSnackbar({
        open: true,
        msg: successMessage,
        severity: "success",
      });
      fetchCommands(true);
    } catch (error) {
      console.error("Failed to send device command", error);
      const message =
        error?.response?.data?.error ||
        `Unable to send ${type.replaceAll("_", " ")} command`;
      setSnackbar({
        open: true,
        msg: message,
        severity: "error",
      });
    } finally {
      setActionLoading("");
    }
  };

  const relayTiles = Array.from({ length: relayCount || 0 }, (_, index) => {
    console.log(index, relayCount, 277);
    
    const relayNumber = index + 1;
    return {
      relayNumber,
      loadingOn: `relay${relayNumber}_on`,
      loadingOff: `relay${relayNumber}_off`,
    };
  });

  if (!canView) {
    return null;
  }

  return (
    <Box>
      <Box>
        <Box
          sx={{
            mb: 3,
            p: 2.25,
            borderRadius: "18px",
            border: "1px solid #BFDBFE",
            background:
              "linear-gradient(135deg, rgba(239,246,255,0.95), rgba(236,253,245,0.92))",
            boxShadow: "0 18px 40px rgba(37, 99, 235, 0.06)",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
          >
            <Box>
              <Typography
                sx={{ fontWeight: 800, color: "#0F172A", fontSize: 16 }}
              >
                Remote Command Console
              </Typography>
              <Typography sx={{ mt: 0.5, color: "#475569", fontSize: 13 }}>
                Use this workspace for live device operations. Commands are
                published on MQTT, and the history below tracks acknowledgements
                and completion updates from the device.
              </Typography>
            </Box>
            <Chip
              label={canControl ? "Control enabled" : "Read-only access"}
              sx={{
                fontWeight: 800,
                backgroundColor: canControl ? "#DCFCE7" : "#E2E8F0",
                color: canControl ? "#166534" : "#475569",
              }}
            />
          </Stack>
        </Box>

        <Box sx={{ mb: 2.5, p: 2, borderRadius: "14px", border: "1px solid #E2E8F0", background: "#FFFFFF" }}>
          <Typography sx={{ fontWeight: 800, color: "#0F172A" }}>Command signing</Typography>
          <Typography sx={{ mt: 0.4, mb: 1.5, color: "#64748B", fontSize: 13 }}>
            Choose the format for platform-to-device MQTT messages, including remote commands and OTA updates. HMAC devices must be provisioned with the same secret.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
            <TextField select size="small" label="Signing mode" value={signingMode} onChange={(event) => setSigningMode(event.target.value)} disabled={!canControl || securitySaving} SelectProps={{ native: true }} sx={{ minWidth: 180 }}>
              <option value="NONE">Current (default)</option>
              <option value="HMAC">HMAC-SHA256</option>
            </TextField>
            {signingMode === "HMAC" ? (
              <TextField size="small" label="New shared secret (optional)" value={hmacSecret} onChange={(event) => setHmacSecret(event.target.value)} disabled={!canControl || securitySaving} type="password" helperText={hmacConfigured ? "Leave blank to keep the existing key" : "Leave blank to generate a secure key"} sx={{ flex: 1 }} />
            ) : null}
            <Button variant="contained" onClick={saveCommandSecurity} disabled={!canControl || securitySaving} sx={{ textTransform: "none", borderRadius: "10px" }}>
              {securitySaving ? <CircularProgress size={16} /> : "Save signing mode"}
            </Button>
          </Stack>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr", // 📱 1 per row
              md: "1fr 1fr", // 💻 2 per row
            },
            gap: 2,
          }}
        >
          <ActionTile
            icon={<WifiTethering fontSize="small" />}
            title="Ping Device"
            desc="Send a reachability check and wait for a device acknowledgement."
            busy={actionLoading === "ping"}
            primaryLabel="Send ping"
            onPrimary={() =>
              sendCommand("ping", {}, "ping", "Ping command sent to device")
            }
            primaryDisabled={!canControl}
          />

          <ActionTile
            icon={<RestartAlt fontSize="small" />}
            title="Restart Device"
            desc="Request a controlled software reboot from the server side."
            busy={actionLoading === "reboot"}
            primaryLabel="Restart"
            onPrimary={() =>
              sendCommand(
                "reboot",
                {},
                "reboot",
                "Restart command sent to device",
              )
            }
            primaryDisabled={!canControl}
          />

          {relayCount === null ? (
            <Box
              sx={{
                gridColumn: "1 / -1",
                p: 2,
                borderRadius: "14px",
                border: "1px dashed #CBD5E1",
                background: "#FAFBFF",
              }}
            >
              <Typography sx={{ fontWeight: 700, color: "#0F172A" }}>
                Loading relay configuration...
              </Typography>
              <Typography sx={{ mt: 0.6, color: "#64748B", fontSize: 13 }}>
                Reading the device type relay count before showing controls.
              </Typography>
            </Box>
          ) : (
            relayTiles.map(({ relayNumber, loadingOn, loadingOff }) => (
              <ActionTile
                key={relayNumber}
                icon={
                  relayNumber % 2 === 0 ? (
                    <Sensors fontSize="small" />
                  ) : (
                    <PowerSettingsNew fontSize="small" />
                  )
                }
                title={`Relay ${relayNumber}`}
                desc={`Toggle Relay ${relayNumber} using the command channel.`}
                busy={actionLoading === loadingOn || actionLoading === loadingOff}
                primaryLabel="Turn ON"
                secondaryLabel="Turn OFF"
                onPrimary={() =>
                  sendCommand(
                    "relay_control",
                    { relay: relayNumber, state: true },
                    loadingOn,
                    `Relay ${relayNumber} ON command sent`,
                  )
                }
                onSecondary={() =>
                  sendCommand(
                    "relay_control",
                    { relay: relayNumber, state: false },
                    loadingOff,
                    `Relay ${relayNumber} OFF command sent`,
                  )
                }
                primaryDisabled={!canControl}
                secondaryDisabled={!canControl}
              />
            ))
          )}
        </Box>

        <Box
          sx={{
            mt: 2.5,
            p: 2,
            borderRadius: "14px",
            border: "1px solid #E2E8F0",
            background: "#F8FAFC",
          }}
        >
          <Typography
            sx={{
              fontSize: 12,
              color: "#64748B",
              fontWeight: 800,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
          >
            Command Topics
          </Typography>
          <Stack spacing={0.75} sx={{ mt: 1.25 }}>
            <Typography
              sx={{ fontFamily: "monospace", color: "#0F172A", fontSize: 13 }}
            >
              Publish: {topics.command_topic || `devices/${deviceId}/commands`}
            </Typography>
            <Typography
              sx={{ fontFamily: "monospace", color: "#0F172A", fontSize: 13 }}
            >
              Ack:{" "}
              {topics.command_ack_topic || `devices/${deviceId}/commands/ack`}
            </Typography>
          </Stack>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={1}
        >
          <Box>
            <Typography
              sx={{ fontWeight: 800, color: "#0F172A", fontSize: 18 }}
            >
              Command History
            </Typography>
            <Typography sx={{ mt: 0.4, color: "#64748B", fontSize: 13 }}>
              Recent command activity and device acknowledgements.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={`${commands.length} recent command${commands.length === 1 ? "" : "s"}`}
              sx={{
                fontWeight: 700,
                backgroundColor: "#EFF6FF",
                color: "#1D4ED8",
              }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={() => fetchCommands()}
              startIcon={<Autorenew />}
              sx={{
                borderRadius: "999px",
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>

        <Stack spacing={1.5} sx={{ mt: 2 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress />
            </Box>
          ) : commands.length === 0 ? (
            <Box
              sx={{
                p: 2.5,
                borderRadius: "14px",
                border: "1px dashed #CBD5E1",
                background: "#FAFBFF",
              }}
            >
              <Typography sx={{ fontWeight: 700, color: "#0F172A" }}>
                No commands sent yet
              </Typography>
              <Typography sx={{ mt: 0.6, color: "#64748B", fontSize: 13 }}>
                Use the actions above to send ping, reboot, or relay commands to
                this device. Relay controls are shown based on the device's configured relay count.
              </Typography>
            </Box>
          ) : (
            commands.map((command) => {
              const tone = statusTone[
                String(command?.status || "").toUpperCase()
              ] || {
                bg: "#E2E8F0",
                color: "#475569",
              };

              return (
                <Box
                  key={command.id}
                  sx={{
                    p: 2,
                    borderRadius: "14px",
                    border: "1px solid #E2E8F0",
                    background: "#FFFFFF",
                  }}
                >
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={1.5}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", md: "center" }}
                  >
                    <Box>
                      <Typography sx={{ fontWeight: 800, color: "#0F172A" }}>
                        {String(command.command_type || "")
                          .replaceAll("_", " ")
                          .toUpperCase()}
                      </Typography>
                      <Typography
                        sx={{ mt: 0.5, color: "#64748B", fontSize: 13 }}
                      >
                        {summarizePayload(command)}
                      </Typography>
                    </Box>
                    <Chip
                      label={command.status || "UNKNOWN"}
                      sx={{
                        fontWeight: 800,
                        backgroundColor: tone.bg,
                        color: tone.color,
                      }}
                    />
                  </Stack>

                  <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} md={4}>
                      <Typography
                        sx={{ fontSize: 12, color: "#94A3B8", fontWeight: 700 }}
                      >
                        CREATED
                      </Typography>
                      <Typography
                        sx={{ fontSize: 13, color: "#0F172A", mt: 0.35 }}
                      >
                        {formatTimestamp(command.created_at)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography
                        sx={{ fontSize: 12, color: "#94A3B8", fontWeight: 700 }}
                      >
                        ACKNOWLEDGED
                      </Typography>
                      <Typography
                        sx={{ fontSize: 13, color: "#0F172A", mt: 0.35 }}
                      >
                        {formatTimestampAdd530(command.acknowledged_at)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography
                        sx={{ fontSize: 12, color: "#94A3B8", fontWeight: 700 }}
                      >
                        COMPLETED
                      </Typography>
                      <Typography
                        sx={{ fontSize: 13, color: "#0F172A", mt: 0.35 }}
                      >
                        {formatTimestampAdd530(command.completed_at)}
                      </Typography>
                    </Grid>
                  </Grid>

                  {command.message || command.error_message ? (
                    <Box
                      sx={{
                        mt: 1.5,
                        p: 1.5,
                        borderRadius: "12px",
                        backgroundColor: command.error_message
                          ? "#FEF2F2"
                          : "#F8FAFC",
                        border: "1px solid",
                        borderColor: command.error_message
                          ? "#FECACA"
                          : "#E2E8F0",
                      }}
                    >
                      <Typography sx={{ fontSize: 13, color: "#0F172A" }}>
                        {command.error_message || command.message}
                      </Typography>
                    </Box>
                  ) : null}
                </Box>
              );
            })
          )}
        </Stack>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2600}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
